import { useState, useCallback } from 'react';
import { linkQueueItem, createQueueResource } from './services/queue.service';
import {
  dismissReviewItem,
  getMigrationStatus,
  resolveReviewItem,
} from './services/completion-rule-migration.service';
import { QUEUE_STATUS, COMPLETION_RULES_QUEUED_TOAST } from './constants/lms-course-review.constants';
import useResourceSearchHook from './useResourceSearchHook';

const toResourceOption = (resource) => ({
  id: resource.id ?? resource.resourceKey,
  resourceKey: resource.resourceKey,
  name: resource.name ?? resource.resourceName,
  label: resource.name ?? resource.resourceName,
  externalId: resource.externalId,
  description: resource.description,
  score: resource.score ?? resource.similarityScore,
});

const resolveErrorMessage = (error, actionLabel) => {
  const detail = error?.message || error?.body?.message;
  if (detail && String(detail).toLowerCase().includes('writeback')) {
    return `${actionLabel} failed: Canvas writeback error. The queue item remains pending.`;
  }
  return `${actionLabel} failed. Please try again.`;
};

/** Map GET status response → MigrationReportPanel shape. */
const toMigrationPanelData = (view) => {
  if (!view) return null;
  return {
    status: view.migrationStatus ?? view.status ?? null,
    report: view.report ?? null,
  };
};

const useResolutionDialogHook = ({ onResolved, onToast }) => {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);
  const [createForm, setCreateForm] = useState({ name: '', externalId: '', description: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [migrationDetail, setMigrationDetail] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [reviewActionRuleId, setReviewActionRuleId] = useState(null);

  const { options: searchOptions, loading: searchLoading } = useResourceSearchHook(searchInput);

  // Use the suggestion already provided by the queue List API. We intentionally
  // do NOT call search-resources to synthesize a suggestion — that endpoint is
  // reserved for explicit manual searches by the admin.
  const applySuggestion = useCallback((queueItem) => {
    setSuggestion(
      queueItem.suggestedResource ? toResourceOption(queueItem.suggestedResource) : null
    );
  }, []);

  const loadMigrationDetail = useCallback(async (resourceId) => {
    if (resourceId == null) {
      setMigrationDetail(null);
      return;
    }
    setMigrationLoading(true);
    try {
      const view = await getMigrationStatus(resourceId);
      setMigrationDetail(toMigrationPanelData(view));
    } catch {
      // Fall back to list status-only snapshot if detail fetch fails.
      setMigrationDetail(null);
    } finally {
      setMigrationLoading(false);
    }
  }, []);

  const openDialog = useCallback(
    (queueItem) => {
      setItem(queueItem);
      setSuggestion(null);
      setSuggestionDismissed(false);
      setSearchInput('');
      setSelectedResource(null);
      setCreateForm({
        name: queueItem.canvasCourseName || '',
        externalId: queueItem.canvasCourseId || '',
        description: queueItem.description || '',
      });
      setMigrationDetail(null);
      setOpen(true);

      if (queueItem.status === QUEUE_STATUS.PENDING) {
        applySuggestion(queueItem);
      }

      if (
        queueItem.status === QUEUE_STATUS.RESOLVED &&
        queueItem.linkedResourceId != null
      ) {
        // Seed with list status so the panel is not empty while loading.
        if (queueItem.completionRuleMigration?.status) {
          setMigrationDetail({
            status: queueItem.completionRuleMigration.status,
            report: null,
          });
        }
        void loadMigrationDetail(queueItem.linkedResourceId);
      }
    },
    [applySuggestion, loadMigrationDetail]
  );

  const closeDialog = useCallback(() => {
    setOpen(false);
    setItem(null);
    setSuggestion(null);
    setSuggestionDismissed(false);
    setSearchInput('');
    setSelectedResource(null);
    setCreateForm({ name: '', externalId: '', description: '' });
    setMigrationDetail(null);
    setMigrationLoading(false);
  }, []);

  const dismissSuggestion = useCallback(() => {
    setSuggestionDismissed(true);
    setSuggestion(null);
  }, []);

  const acceptSuggestion = useCallback(async () => {
    const resourceKey = suggestion?.resourceKey ?? suggestion?.id;
    if (!item || !resourceKey) return;
    setActionLoading(true);
    try {
      await linkQueueItem(item.id, { resourceKey });
      onToast({
        type: 'success',
        message: `Queue item linked and resolved. ${COMPLETION_RULES_QUEUED_TOAST}`,
      });
      closeDialog();
      onResolved();
    } catch (error) {
      onToast({ type: 'error', message: resolveErrorMessage(error, 'Link') });
    } finally {
      setActionLoading(false);
    }
  }, [item, suggestion, onResolved, onToast, closeDialog]);

  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
    setSelectedResource(null);
  }, []);

  const handleSelectResource = useCallback((option) => {
    setSelectedResource(toResourceOption(option));
  }, []);

  const clearSelectedResource = useCallback(() => {
    setSelectedResource(null);
  }, []);

  const linkSelectedResource = useCallback(async () => {
    const resourceKey = selectedResource?.resourceKey ?? selectedResource?.id;
    if (!item || !resourceKey) return;
    setActionLoading(true);
    try {
      await linkQueueItem(item.id, { resourceKey });
      onToast({
        type: 'success',
        message: `Queue item linked and resolved. ${COMPLETION_RULES_QUEUED_TOAST}`,
      });
      closeDialog();
      onResolved();
    } catch (error) {
      onToast({ type: 'error', message: resolveErrorMessage(error, 'Link') });
    } finally {
      setActionLoading(false);
    }
  }, [item, selectedResource, onResolved, onToast, closeDialog]);

  const createAndLink = useCallback(async () => {
    if (!item || !createForm.name.trim()) return;
    setActionLoading(true);
    try {
      await createQueueResource(item.id, {
        name: createForm.name.trim(),
        externalId: createForm.externalId.trim() || undefined,
        description: createForm.description.trim() || undefined,
      });
      onToast({
        type: 'success',
        message: 'Course created and queue item resolved.',
      });
      closeDialog();
      onResolved();
    } catch (error) {
      onToast({ type: 'error', message: resolveErrorMessage(error, 'Create and link') });
    } finally {
      setActionLoading(false);
    }
  }, [item, createForm, onResolved, onToast, closeDialog]);

  const handleResolveReviewItem = useCallback(
    async (ruleId) => {
      const resourceId = item?.linkedResourceId;
      if (resourceId == null || !ruleId) return;
      setReviewActionRuleId(ruleId);
      try {
        await resolveReviewItem(resourceId, ruleId);
        onToast({ type: 'success', message: 'Review item resolved.' });
        await loadMigrationDetail(resourceId);
      } catch (error) {
        onToast({ type: 'error', message: resolveErrorMessage(error, 'Resolve review item') });
      } finally {
        setReviewActionRuleId(null);
      }
    },
    [item, loadMigrationDetail, onToast]
  );

  const handleDismissReviewItem = useCallback(
    async (ruleId) => {
      const resourceId = item?.linkedResourceId;
      if (resourceId == null || !ruleId) return;
      setReviewActionRuleId(ruleId);
      try {
        await dismissReviewItem(resourceId, ruleId);
        onToast({ type: 'success', message: 'Review item dismissed.' });
        await loadMigrationDetail(resourceId);
      } catch (error) {
        onToast({ type: 'error', message: resolveErrorMessage(error, 'Dismiss review item') });
      } finally {
        setReviewActionRuleId(null);
      }
    },
    [item, loadMigrationDetail, onToast]
  );

  const isPending = item?.status === QUEUE_STATUS.PENDING;
  const showSuggestion = Boolean(isPending && suggestion && !suggestionDismissed);

  return {
    dialog: {
      isOpen: open,
      item,
      isPending,
      open: openDialog,
      close: closeDialog,
    },
    suggestion: {
      data: suggestion,
      show: showSuggestion,
      dismiss: dismissSuggestion,
      accept: acceptSuggestion,
    },
    search: {
      input: searchInput,
      setInput: handleSearchInputChange,
      options: searchOptions,
      loading: searchLoading,
      selectedResource,
      select: handleSelectResource,
      clear: clearSelectedResource,
      linkSelected: linkSelectedResource,
    },
    createForm: {
      values: createForm,
      setValues: setCreateForm,
      submit: createAndLink,
    },
    migration: {
      data: migrationDetail,
      loading: migrationLoading,
      reload: () =>
        item?.linkedResourceId != null
          ? loadMigrationDetail(item.linkedResourceId)
          : Promise.resolve(),
      resolveReviewItem: handleResolveReviewItem,
      dismissReviewItem: handleDismissReviewItem,
      reviewActionRuleId,
    },
    actionLoading,
  };
};

export default useResolutionDialogHook;
